import { CobaltDivider } from '@cae/cobalt-react/components/divider';
import { CobaltIconCommentOutline } from '@cae/cobalt-react/icons/outline/CobaltIconCommentOutline';
import { CobaltIconEmailOutline } from '@cae/cobalt-react/icons/outline/CobaltIconEmailOutline';
import { CobaltIconPhoneOutline } from '@cae/cobalt-react/icons/outline/CobaltIconPhoneOutline';
import { navigateToHelp } from '@shared/helpers/navigate-to-help/navigateToHelp';
import React from 'react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import welcomeFooterSwirls from '../../assets/welcome-footer-swirls.png';
import styles from './LandingNeedHelp.module.css';

const LandingNeedHelp = React.memo((): React.ReactElement => {
  const { t, i18n } = useTranslation();
  const [language, setLanguage] = useState(i18n.language);

  const handleTranslation = useCallback((): void => {
    const changeLanguageTo = language === 'en' ? 'fr' : 'en';
    setLanguage(changeLanguageTo);
    i18n.changeLanguage(changeLanguageTo);
  }, [language, i18n]);

  const handleHelpClick = useCallback((): void => {
    navigateToHelp(true);
  }, []);

  return (
    <div
      className={styles.landingNeedHelp}
      data-testid="landing-need-help"
      style={
        {
          '--welcome-footer-swirls': `url(${welcomeFooterSwirls})`,
        } as React.CSSProperties
      }
    >
      <div className={styles.landingNeedHelpTop}>
        <h2>{t('common.needHelp')}</h2>
        <p>{t('landingPage.helpDescription')}</p>
      </div>
      <div className={styles.landingNeedHelpContent}>
        <button
          className={styles.landingNeedHelpContentCard}
          onClick={handleHelpClick}
        >
          <h3>{t('common.callUs')}</h3>
          <CobaltIconPhoneOutline
            className={styles.landingNeedHelpButtonIcon}
          />
        </button>
        <button
          className={styles.landingNeedHelpContentCard}
          onClick={handleHelpClick}
        >
          <h3>{t('common.messageUs')}</h3>
          <CobaltIconEmailOutline
            className={styles.landingNeedHelpButtonIcon}
          />
        </button>
        <button
          className={styles.landingNeedHelpContentCard}
          onClick={handleHelpClick}
        >
          <h3>{t('common.chatWithUs')}</h3>
          <CobaltIconCommentOutline
            className={styles.landingNeedHelpButtonIcon}
          />
        </button>
      </div>
      <div className={`${styles.landingNeedHelpFooter} body-01`}>
        <span>©{t('landingPage.caeInc')}</span>
        <span className={styles.landingNeedHelpFooterLinks}>
          <button type="button" rel="noreferrer" onClick={handleTranslation}>
            {t('common.changeLanguageTo')}
          </button>
          <CobaltDivider orientation="vertical" />
          <a
            href={
              language === 'fr'
                ? 'https://www.cae.com/fr/modalites-dutilisation/'
                : 'https://www.cae.com/terms-of-use-agreement/'
            }
            target="_blank"
            rel="noreferrer"
          >
            {t('landingPage.legalNotice')}
          </a>{' '}
          <CobaltDivider orientation="vertical" />
          <a
            href={
              language === 'fr'
                ? 'https://www.cae.com/fr/politique-de-protection-de-la-vie-privee/'
                : 'https://www.cae.com/privacy-policy/'
            }
            target="_blank"
            rel="noreferrer"
          >
            {t('landingPage.caePrivacy')}
          </a>{' '}
          <CobaltDivider orientation="vertical" />
          <a href={'/cookie-policy/'} target="_blank" rel="noreferrer">
            {t('landingPage.cookiePolicy')}
          </a>{' '}
          <CobaltDivider orientation="vertical" />
          <button
            type="button"
            rel="noreferrer"
            className={`${styles.landingNeedHelpChangeCookieSettings} ot-sdk-show-settings`}
          >
            {t('landingPage.changeCookieSettings')}
          </button>
        </span>
      </div>
    </div>
  );
});

LandingNeedHelp.displayName = 'LandingNeedHelp';

export { LandingNeedHelp };
